import { useState } from 'react'
import { isApiError } from '@/api/http'
import { useSaveAssessment, type AssessmentCell } from '@/api/clients/assessmentsApi'
import { RdModal } from '@/components/readiness/RdModal'

// ── Save assessment (readiness service) ─────────────────────────────────────
// Saving keeps today's levels as a dated, read-only record for audit and
// comparison, then work continues in a working draft with the same levels. An
// objective still waiting on a suggestion or a carried-over level must be
// reviewed first; one without evidence only warns.

const today = () => new Date().toISOString().slice(0, 10)
const quarterTitle = () => {
  const d = new Date()
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()} assessment`
}

export function SaveAssessmentModal({ orgId, assessmentId, cells, onReview, onClose, onSaved }: { orgId: string; assessmentId: string; cells: AssessmentCell[]; onReview: () => void; onClose: () => void; onSaved: (title: string) => void }) {
  const [title, setTitle] = useState(quarterTitle)
  const [asOf, setAsOf] = useState(today)
  const save = useSaveAssessment(orgId)
  const waiting = cells.filter((c) => c.review_reason === 'suggestion' || c.review_reason === 'carried_forward')
  const noEvidence = cells.filter((c) => c.review_reason === 'no_evidence')
  const error = save.error == null ? null
    : isApiError(save.error, 'ASSESSOR_REQUIRED') ? 'Only an assessor or an administrator can save an assessment.'
    : save.error instanceof Error ? save.error.message : String(save.error)
  return (
    <RdModal
      title="Save assessment"
      subtitle="Keep today's levels as a dated record for audit and comparison. You carry on working with the same levels."
      onClose={onClose}
      testId="save-assessment-modal"
      footerClassName="justify-end gap-2"
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="rd-btn rd-btn-primary"
            disabled={waiting.length > 0 || !title.trim() || save.isPending}
            onClick={() => save.mutate({ assessmentId, title: title.trim(), as_of_date: asOf }, { onSuccess: () => onSaved(title.trim()) })}
            data-testid="save-assessment-confirm"
          >
            Save
          </button>
        </>
      }
    >
      {waiting.length > 0 ? (
        <p className="mb-3 rounded-[8px] px-3 py-2 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }}>
          Review {waiting.map((c) => c.objective_code).join(', ')} first.{' '}
          <button type="button" className="rd-link" onClick={onReview}>Review changes →</button>
        </p>
      ) : noEvidence.length > 0 ? (
        <p className="mb-3 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
          {noEvidence.map((c) => c.objective_code).join(', ')} {noEvidence.length === 1 ? 'has' : 'have'} no evidence yet. You can still save.
        </p>
      ) : null}
      <label htmlFor="rd-save-title" className="rd-label block">Name</label>
      <input id="rd-save-title" className="rd-input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="save-assessment-title" />
      <label htmlFor="rd-save-asof" className="rd-label mt-3 block">Levels as of</label>
      <input id="rd-save-asof" type="date" className="rd-input mt-1" value={asOf} max={today()} onChange={(e) => setAsOf(e.target.value)} />
      <p className="mt-3 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Once saved, this record can't be changed. Later changes go into the working draft and the next saved assessment.</p>
      {error && <p className="mt-2 text-[12px]" style={{ color: 'var(--rd-status-warn)' }} role="alert">{error}</p>}
    </RdModal>
  )
}
